import ol = require("ol");
import _ = require("underscore");
import Portal = require("portal-gis");
import Utils = require("../utils");
import geo = require("../utils/geo");
import Mediator = require("../mediator");
import IPS = require("publicsector");
import MapService = require("../services/ags/map-service-reader");
import Base = require("./base/geoquery");
import KeywordValidator = require("./base/keywords");
const log = Utils.log;

function doif<T>(v: T, cb: (v: T) => void) {
    if (typeof v !== "undefined") cb(v);
}

class Command extends Base<{
    controller: any;
    out: {
        location: string;
        layers: any;
    }
}> {

    static about = `
Uses the ArcGIS Server _find_ a _query_ services implemented by _services/ags/map-service-reader_ to perform spatial and non-spatial queries.

Handles three types of commands:

| type | about |
| --- | --- |
| find | Invokes the "find" service of ArcGIS Server.  See the "ARCGIS REST API-FIND":http://resources.arcgis.com/en/help/arcgis-rest-api/index.html#//02r3000000zm000000. |
| join | Requires "query-service" and "service-query-template" option to perform one query per input layer.  Each query is build from all provided features concatenated with "OR". |
| spatial | Requires "query-service" option an a "box" or "point" argument to execute a spatial filter, which does not contain a data filtering component  |

If no type is provided The default is "spatial", but the presence of a "service-query-template" option indicates "join".  If that option is not missing and it is called with a "location" argument then it assumes the type is "find".

Usage Example:

\`\`\`xml
<EVENT about="Use the AGS find service to search for parcels" disabled="False" event="universal-search" id="ags-parcel-locator" mid="app/commands/ags-geoquery-locator" text="Parcel Locator" type="find">
  <OPTIONS>
    <Values id="query-service" value="https://usalvqa1.infor.com/arcgis/rest/services/IPS850/QASQL4850/MapServer/16&amp;sr=4326" />
    <Values about="Directs the renderer to use the 'parcels' symbologies instead of the default (*) symbologies" id="symbology" value="parcels" />
    <Values id="zoommap" value="true" />
    <Values id="key-template" value="&lt;%=FID%&gt;" />
    <Values about="Once the results are gathered forward them to the 'geoquery-result' handler" id="event" value="add-to-parcel-layer,auto-zoom" />
    <Values about="merge features into the parcel-features layer" id="layer-name" value="parcel-features" />
    <Values about="do a parcel search during a universal-search event and associate the results to the _parcel_ keyword" id="keywords" value="parcel" />
  </OPTIONS>
</EVENT>
\`\`\`

`;

    // the options immediately assigned to this command    
    static options = {
        "box": {
            "about": "search area as a rectangle",
        },
        "buffer-size": {
            "about": "grow the search extent but this many pixels",
            "default": "8",
        },
        "event": {
            "about": "the event to trigger",
            "default": "geoquery-result",
        },
        "filter": {
            "about": "data filter",
        },
        "key-template": {
            "about": "template for computing the features key value",
            "default": "<%=OBJECTID%>",
        },
        "keywords": {
            "about": "when present prevents execution when message bus contains keywords and none match; facilitating execution scope (e.g. universal-search can be configured to perform 'parcel' searches by providing a _parcel_ keyword).",
        },
        "layers": {
            "about": `
            | type  | about |
            | find  | list of layers to search when doing a 'find' |
            | join  | all features from each layer are run through the query template to perform the join |
            | spatial | spatial join using the first feature in the first layer |
            `,
        },
        "layerDefs": {
            "about": "restrict search to these layers",
        },
        "location": {
            "about": "the search text",
        },
        "max-feature-count": {
            "about": "when present, _find_ and _query_ both reduce the result set to this count",
        },
        "pixel": {
            "about": "search location as a pixel",
        },
        "point": {
            "about": "search location as a point",
        },
        "queryService": {
            "about": "the ags rest endpoint",
        },
        "service-query-template": {
            "about": "the query template for a 'join' request",
        },
        "type": {
            "about": "Identifies the query type",
            "domain": "find|join|spatial",
            "default": "spatial",
        }
    };

    // values available as input to the next handler (via args.out)    
    static output = {
        "filterInfo": "the input filter options (url, searchText, layers)",
        "layers": "the results of the 'find' or 'query'",
        "metadata": "the fields of a query response",
        "query": "the 'join' OR clause",
        "serviceInfo": "the options of this commands",
        "typeName": "the filterInfo layers",
        "url": "the service endpoint",
    };

    // option - data filter    
    get filter() {
        return this.options["filter"];
    }

    // option - list of layers to search when doing a "find"    
    get layers() {
        return this.options["layers"];
    }

    // option - restrict search to these layers
    get layerDefs() {
        return this.options["layerDefs"];
    }

    // the query template for a 'join' request
    get queryServiceTemplate() {
        return this.options["service-query-template"];
    }

    // option - the ags rest endpoint
    get queryService() {
        return this.options["query-service"];
    }

    execute(args = this.args) {

        if (!KeywordValidator.validate(this)) return;

        // type option trumps type attribute
        let queryType = this.configuration.type;

        Utils.ifOption(this.options, "type", v => {
            queryType = v;
        });

        if (!queryType) {
            queryType = Command.options.type.default;
            if (!!this.queryServiceTemplate) queryType = "join";
            else if (this.findOption("location")) queryType = "find";
        }

        switch (queryType) {
            case "find":
                doif(this.findOption("location"), v => this.executeFind({
                    location: v
                }));
                break;
            case "join":
                doif(this.findArg("layers", this.args).layers, v => this.executeJoinQuery({
                    layers: v
                }));
                break;
            case "spatial":
            default:
                // provide the map to compute extents from pixels
                this.mediator.mapViewMap(map => {

                    this.executeSpatialQuery({
                        map: map,
                        pixel: this.findOption("pixel"),
                        box: this.findOption("box"),
                        point: this.findOption("point"),
                        layers: this.findOption("layers")
                    });

                });
                break;
        }

    }

    // find service using searchText
    private executeFind(args: {
        location: string;
    }) {

        let options = this.options;

        Utils.ifOption(options, "query-service", url => {

            var filterInfo = {
                url: url,
                searchText: args.location,
                layers: this.layers,
                options: options
            };

            // two ways of expressing the target layer(s)
            if (!filterInfo.layers) {
                var urlParts = filterInfo.url.split("/");
                filterInfo.layers = urlParts.pop();
                filterInfo.url = urlParts.join("/");
            }

            MapService.find(filterInfo).done(result => {

                let maxFeatureCount = parseInt(this.findOption("max-feature-count") || "-1");
                if (0 < maxFeatureCount && maxFeatureCount < result.features.length) {
                    result.features = result.features.slice(0, maxFeatureCount);
                }
                this.assignKeys(result);

                this.trigger({
                    filterInfo: filterInfo,
                    serviceInfo: options,
                    layers: [result]
                });

            });

        });

    }

    // query service using geometry
    private executeSpatialQuery(args: {
        map: ol.Map;
        point?: ol.Coordinate;
        box?: ol.Coordinate;
        pixel?: ol.Pixel;
        layers?: number[];
    }) {
        var options = this.options;

        let spatialFilterLayers = <Rhythm.GIS.FeatureQueryResultLayer[]>this.findArg("layers", this.args).layers;
        let activeFeature = <Rhythm.GIS.FeatureQueryResultFeature>this.findArg("activeFeature", this.args).activeFeature;

        Utils.ifOption(options, "query-service", url => {

            var filterInfo = {
                url: url,
                geometry: "",
                geometryType: "",
                layers: this.layers,
                filter: this.filter,
                options: options
            };

            if (!!spatialFilterLayers && Array.isArray(spatialFilterLayers) && spatialFilterLayers.some(l => l.features.some(f => {
                switch (f.geometryType) {
                    case "polygon":
                        let foo = Utils.extend(f);
                        let geom = f.geometry;
                        let g = new ol.geom.Polygon(geom);
                        g = <any>g.simplify(0.00001);
                        filterInfo.geometry = `{rings:${JSON.stringify(g.getCoordinates())}}`;
                        filterInfo.geometryType = "esriGeometryPolygon";
                        return true;
                }
                return false;
            }))) {
                // filterInfo already set
            }
            else if (args.pixel && (this.options["buffer-size"] || !args.point && !args.box)) {
                let map = args.map;
                let pixel = args.pixel;
                let bufferSize = 8;

                Utils.ifOption(this.options, "buffer-size", v => bufferSize = parseInt(v, 10));
                filterInfo.geometry = geo.pixelToExtent(map, pixel, bufferSize).join(",");
                filterInfo.geometryType = "esriGeometryEnvelope";
            }
            else if (args.point) {
                filterInfo.geometry = args.point.join(",");
                filterInfo.geometryType = "esriGeometryPoint";
            }
            else if (args.box) {
                filterInfo.geometry = args.box.join(",");
                filterInfo.geometryType = "esriGeometryEnvelope";
            }
            // if there is no box and no point then look for an activeFeature
            else if (activeFeature) {
                switch (activeFeature.geometryType) {
                    case "point":
                        filterInfo.geometry = activeFeature.geometry.toString();
                        filterInfo.geometryType = "esriGeometryPoint";
                        break;
                    case "polygon":
                        let geom = activeFeature.geometry;
                        let g = new ol.geom.Polygon(geom);
                        g = <any>g.simplify(0.00001);
                        filterInfo.geometry = `{rings:${JSON.stringify(g.getCoordinates())}}`;
                        filterInfo.geometryType = "esriGeometryPolygon";
                        break;
                    default:
                        throw `unknown geometry type: ${activeFeature.geometryType}`;
                }
            }
            else {
                throw "unable to determine geometry of spatial filter";
            }

            // two ways of expressing the target layer(s)
            if (!filterInfo.layers) {
                var urlParts = filterInfo.url.split("/");
                filterInfo.layers = urlParts.pop();
                filterInfo.url = urlParts.join("/");
            }

            if (!filterInfo.filter) {
                let filter = this.layerDefs;
                if (filter) {
                    filter = JSON.parse(filter)[filterInfo.layers];
                    filterInfo.filter = filter;
                }
            }

            // this needs to stay consistent with ags-geoquery-processor
            MapService.query(filterInfo).done(result => {

                let maxCount = parseInt(this.options["max-feature-count"] || "0");
                if (maxCount && maxCount < result.features.length) {
                    log(`reducing result set to ${maxCount} items`);
                    result.features.splice(maxCount - 1, result.features.length - maxCount);
                }

                this.assignKeys(result);

                Utils.ifOption(options, "symbology", v => Utils.extend(result, { symbology: v }));

                this.trigger({
                    filterInfo: null, // spatial only
                    layers: [result],
                    metadata: result.fields,
                    query: null, // spatial only
                    serviceInfo: null,
                    typeName: filterInfo.layers,
                    url: filterInfo.url
                });
            });
        });
    }

    // query service using relational search (e.g. find addresses associated with a parcel, see A-0770)
    private executeJoinQuery(args: {
        layers: Rhythm.GIS.FeatureQueryResultLayer[];
    }) {
        if (!args.layers) {
            log("no layers to join");
            return;
        }
        var serviceInfo = this.options;

        Utils.ifOption(serviceInfo, "query-service", url => {

            doif(this.queryServiceTemplate, queryTemplate => {

                let template = _.template(queryTemplate);

                let layers = args.layers.filter(l => !!l.features.length);
                if (!layers.length) {
                    log("no features found in any layers");
                    this.trigger();
                }

                layers.forEach(layer => {

                    let data = layer.features.map(f => geo.getFeatureData(f));
                    log("data: ", data);

                    let query: string;
                    try {
                        query = data.map(f => template(f)).join(" OR ");
                        log("query: ", query);
                    } catch (ex) {
                        console.error(ex);
                    }

                    if (!query) return;

                    var filterInfo = {
                        url: url,
                        layers: serviceInfo["layers"],
                        filter: query,
                        options: serviceInfo
                    };

                    // two ways of expressing the target layer(s)
                    if (!filterInfo.layers) {
                        var urlParts = filterInfo.url.split("/");
                        filterInfo.layers = urlParts.pop();
                        filterInfo.url = urlParts.join("/");
                    }

                    // this needs to be consistent with the spatial query
                    MapService.query(filterInfo).done(result => {

                        this.assignKeys(result);
                        Utils.ifOption(serviceInfo, "symbology", v => Utils.extend(result, { symbology: v }));

                        this.trigger({
                            filter: data,
                            layers: [result],
                            metadata: result.fields,
                            query: query,
                            serviceInfo: null,
                            typeName: filterInfo.layers,
                            url: filterInfo.url
                        });
                    });

                });

            });
        });
    }


}

export = Command; 